import React, { useState, useMemo } from 'react';
import { Download, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Machine } from '../../types/map';
import { pdf } from '@react-pdf/renderer';
import { QuotePDF } from '../../calculator/pdf/QuotePDF';
import { Package, Zap, Target, Users, Video, QrCode, Star, Gift, RefreshCw, Shield, BarChart3, UserPlus, ShoppingCart } from 'lucide-react';

interface CatalogItem {
  id: string;
  name: string;
  description: string;
  setupPrice: number;
  recurringPrice: number;
  unit: string;
  category: 'placement' | 'awareness' | 'engagement';
  icon: React.ReactNode;
  image?: string;
  unitType: string;
  chartData?: {
    metrics: Array<{
      label: string;
      value: number | string;
      unit: string;
      color: string;
      icon: React.ReactNode;
    }>;
    timeframe: string;
  };
}

const catalogItems: CatalogItem[] = [
  {
    id: 'dooh-ad',
    name: 'DOOH Ad | 1 Slot/60s',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/2.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Brand Awareness Lift', value: 22, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> },
        { label: 'Total Impressions', value: '~37,500', unit: '/month/machine', color: 'bg-pink-500', icon: <TrendingUp className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'home-banner',
    name: 'Home Banner Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/3.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Brand Awareness Uplift', value: 12, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-icon-lead',
    name: 'Category Icon Lead',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'awareness',
    icon: <Zap className="w-6 h-6" />,
    image: '/calculator-images/4.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 4, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 12, unit: '%', color: 'bg-orange-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'category-banner',
    name: 'Category Banner Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Target className="w-6 h-6" />,
    image: '/calculator-images/5.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share Increase', value: 8, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'premium-listing',
    name: 'Premium Product Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/6.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 18, unit: '%', color: 'bg-blue-500', icon: <ShoppingCart className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'exclusive-category',
    name: 'Exclusive Category',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Category Per Machine',
    category: 'placement',
    icon: <Shield className="w-6 h-6" />,
    image: '/calculator-images/7.png',
    unitType: 'Categories',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 12, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Market Share', value: 6, unit: '%', color: 'bg-blue-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Category Market Share', value: 15, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'enhanced-detail',
    name: 'Enhanced Product Detail',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'engagement',
    icon: <Star className="w-6 h-6" />,
    image: '/calculator-images/8.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'ROS Increase', value: 1.5, unit: '%', color: 'bg-green-500', icon: <TrendingUp className="w-4 h-4" /> },
        { label: 'Conversion Rate', value: 10, unit: '%', color: 'bg-blue-500', icon: <Target className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'vend-video',
    name: 'Vend Video Ad',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'awareness',
    icon: <Video className="w-6 h-6" />,
    image: '/calculator-images/9.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Captive Audience', value: 100, unit: '%', color: 'bg-green-500', icon: <Video className="w-4 h-4" /> },
        { label: 'Brand Awareness Lift', value: 6, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'virtual-sku',
    name: 'Virtual SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Package className="w-6 h-6" />,
    image: '/calculator-images/10.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Conversion Rate', value: '~8', unit: '%', color: 'bg-green-500', icon: <ShoppingCart className="w-4 h-4" /> },
        { label: 'Retention Rate', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'bundle-sku',
    name: 'Bundle SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <Gift className="w-6 h-6" />,
    image: '/calculator-images/11.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Opt In Rate', value: 8, unit: '%', color: 'bg-green-500', icon: <Package className="w-4 h-4" /> },
        { label: 'Intent to Purchase', value: 80, unit: '%', color: 'bg-blue-500', icon: <Gift className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'subscription-sku',
    name: 'Subscription SKU Listing',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per SKU Per Machine',
    category: 'placement',
    icon: <RefreshCw className="w-6 h-6" />,
    image: '/calculator-images/12.png',
    unitType: 'SKUs',
    chartData: {
      metrics: [
        { label: 'Subscription Conversion', value: 4, unit: '%', color: 'bg-green-500', icon: <RefreshCw className="w-4 h-4" /> },
        { label: 'Customer Retention', value: 80, unit: '%', color: 'bg-purple-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'safe-qr',
    name: 'SafeQR Redirect',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per QR Per Machine',
    category: 'engagement',
    icon: <QrCode className="w-6 h-6" />,
    image: '/calculator-images/13.png',
    unitType: 'QR Codes',
    chartData: {
      metrics: [],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-survey',
    name: 'Customer Survey',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Survey Per Machine',
    category: 'engagement',
    icon: <BarChart3 className="w-6 h-6" />,
    image: '/calculator-images/14.png',
    unitType: 'Surveys',
    chartData: {
      metrics: [
        { label: 'Survey Responses', value: 850, unit: '+', color: 'bg-green-500', icon: <BarChart3 className="w-4 h-4" /> },
        { label: 'Completion Rate', value: 52, unit: '%', color: 'bg-blue-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  },
  {
    id: 'customer-signup',
    name: 'Customer Sign-Up',
    description: '',
    setupPrice: 0,
    recurringPrice: 0,
    unit: 'Per Slot Per Machine',
    category: 'engagement',
    icon: <UserPlus className="w-6 h-6" />,
    image: '/calculator-images/15.png',
    unitType: 'Slots',
    chartData: {
      metrics: [
        { label: 'Sign-Up Rate', value: 8, unit: '%', color: 'bg-green-500', icon: <UserPlus className="w-4 h-4" /> },
        { label: 'Customer Acquisition', value: 120, unit: '+', color: 'bg-blue-500', icon: <Users className="w-4 h-4" /> }
      ],
      timeframe: '4 weeks'
    }
  }
];

const locationPricingTiers = [
  {
    id: 'manchester-airport',
    name: 'Manchester Airport Group',
    setupMultiplier: 1,
    recurringMultiplier: 1,
  }
];

interface QuoteOverviewStepProps {
  selectedMachines: Machine[];
  skuCount?: number;
  pricePerSKU?: number;
  retailMediaSelections: Record<string, { machines: number | ''; units: number | '' }>;
}

export function QuoteOverviewStep({
  selectedMachines,
  skuCount = 0,
  pricePerSKU = 0,
  retailMediaSelections,
}: QuoteOverviewStepProps) {
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<4 | 8 | 12>(4);
  const [isGenerating, setIsGenerating] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    fullName: '',
    email: '',
    company: '',
  });

  const selectedLocation = 'manchester-airport';

  const selectedMediaItems = useMemo(() => {
    return Object.entries(retailMediaSelections)
      .filter(([, cfg]) => {
        const machines = typeof cfg.machines === 'number' ? cfg.machines : 0;
        const units = typeof cfg.units === 'number' ? cfg.units : 0;
        return machines > 0 && units > 0;
      })
      .map(([itemId, cfg]) => {
        const item = catalogItems.find(i => i.id === itemId);
        if (!item) return null;
        
        const currentLocationPricing = locationPricingTiers.find(l => l.id === selectedLocation) || locationPricingTiers[0];
        const setupPrice = Math.round(item.setupPrice * currentLocationPricing.setupMultiplier);
        const recurringPrice = Math.round(item.recurringPrice * currentLocationPricing.recurringMultiplier);
        
        const machines = typeof cfg.machines === 'number' ? cfg.machines : 0;
        const units = typeof cfg.units === 'number' ? cfg.units : 0;
        
        return {
          ...item,
          setupPrice,
          recurringPrice,
          machines,
          units,
          totalSetup: setupPrice * machines * units,
          totalRecurring: recurringPrice * machines * units * (selectedTimePeriod / 4),
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        name: string;
        setupPrice: number;
        recurringPrice: number;
        machines: number;
        units: number;
        totalSetup: number;
        totalRecurring: number;
        unitType: string;
        unit: string;
        image?: string;
      }>;
  }, [retailMediaSelections, selectedTimePeriod]);

  const listingFee = useMemo(() => {
    if (skuCount > 0 && pricePerSKU > 0) {
      return skuCount * pricePerSKU * selectedMachines.length;
    }
    return 0;
  }, [skuCount, pricePerSKU, selectedMachines.length]);

  const totals = useMemo(() => {
    const setup = selectedMediaItems.reduce((sum, item) => sum + item.totalSetup, 0);
    const recurring = selectedMediaItems.reduce((sum, item) => sum + item.totalRecurring, 0);
    return { setup, recurring, listingFee, total: setup + recurring + listingFee };
  }, [selectedMediaItems, listingFee]);

  const handleExportPDF = async () => {
    setIsGenerating(true);
    try {
      const selectedItemsData = selectedMediaItems.map((item) => ({
        id: item.id,
        name: item.name,
        unitType: item.unitType,
        unit: item.unit,
        setupPrice: item.setupPrice,
        recurringPrice: item.recurringPrice,
        machines: item.machines,
        units: item.units,
        image: item.image ? `${window.location.origin}${item.image}` : undefined,
      }));

      const doc = (
        <QuotePDF
          locationName={locationPricingTiers.find(l => l.id === selectedLocation)?.name || 'Manchester Airport Group'}
          contact={contactInfo.fullName || contactInfo.email || contactInfo.company ? contactInfo : undefined}
          items={selectedItemsData}
          totals={{ setup: totals.setup, recurring: totals.recurring }}
          timePeriod={selectedTimePeriod}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Campaign-Quote-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Review & Quote</h2>
        <p className="text-slate-600">
          Review your campaign selections and generate a quote.
        </p>
      </div>

      {/* Selected Machines Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Selected Machines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''} selected
            </div>
            
            {/* Group machines by venue type */}
            {(() => {
              const groupedByVenueType = selectedMachines.reduce((acc, machine) => {
                const venueType = machine.venueType || 'Uncategorized';
                if (!acc[venueType]) {
                  acc[venueType] = [];
                }
                acc[venueType].push(machine);
                return acc;
              }, {} as Record<string, typeof selectedMachines>);

              const groupedByVenueName = selectedMachines.reduce((acc, machine) => {
                const venueName = machine.venueName || 'Unnamed Venue';
                if (!acc[venueName]) {
                  acc[venueName] = [];
                }
                acc[venueName].push(machine);
                return acc;
              }, {} as Record<string, typeof selectedMachines>);

              // Show summary by venue type if there are multiple types, otherwise by venue name
              const shouldGroupByVenueType = Object.keys(groupedByVenueType).length > 1;
              const groups = shouldGroupByVenueType ? groupedByVenueType : groupedByVenueName;

              return (
                <div className="space-y-3">
                  {Object.entries(groups).map(([groupName, machines]) => (
                    <div key={groupName} className="border-b pb-3 last:border-0">
                      <div className="font-medium text-slate-900">{groupName}</div>
                      <div className="text-sm text-slate-600 mt-1">
                        {machines.length} machine{machines.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="font-semibold text-slate-900">
                      Total: {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* SKU Selection Summary */}
      {skuCount > 0 && pricePerSKU > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>SKU Listing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {skuCount} SKU{skuCount !== 1 ? 's' : ''} × £{pricePerSKU.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} per SKU
                  </p>
                  <p className="text-xs text-slate-500">
                    {selectedMachines.length} machine{selectedMachines.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600">
                    £{listingFee.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-slate-500">Listing Fee</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Retail Media Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Selected Retail Media</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedMediaItems.length === 0 ? (
            <p className="text-sm text-slate-500">No retail media items selected.</p>
          ) : (
            <div className="space-y-4">
              {selectedMediaItems.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-slate-50 rounded border border-slate-200">
                  <div className="flex items-center gap-3">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                    )}
                    <div>
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-slate-500">
                        {item.machines} machine{item.machines !== 1 ? 's' : ''} × {item.units} {item.unitType.toLowerCase()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-500">Setup: £{item.totalSetup.toLocaleString()}</div>
                    <div className="text-sm text-slate-500">{selectedTimePeriod} weeks: £{item.totalRecurring.toLocaleString()}</div>
                    <div className="font-semibold">£{(item.totalSetup + item.totalRecurring).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Pricing Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2 mb-4">
              {[4, 8, 12].map((weeks) => (
                <button
                  key={weeks}
                  onClick={() => setSelectedTimePeriod(weeks as 4 | 8 | 12)}
                  className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors ${
                    selectedTimePeriod === weeks
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {weeks} Weeks
                </button>
              ))}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Total Setup Cost:</span>
                <span className="font-medium">£{totals.setup.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Total {selectedTimePeriod} Week Cost:</span>
                <span className="font-medium">£{totals.recurring.toLocaleString()}</span>
              </div>
              {totals.listingFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Listing Fee:</span>
                  <span className="font-medium">£{totals.listingFee.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200">
                <span>Total:</span>
                <span className="text-blue-600">£{totals.total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Information (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information (Optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
              <input
                type="text"
                value={contactInfo.fullName}
                onChange={(e) => setContactInfo({ ...contactInfo, fullName: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={contactInfo.email}
                onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
              <input
                type="text"
                value={contactInfo.company}
                onChange={(e) => setContactInfo({ ...contactInfo, company: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                placeholder="Company Name"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleExportPDF}
          disabled={isGenerating || selectedMediaItems.length === 0}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400"
        >
          <Download className="w-4 h-4" />
          {isGenerating ? 'Generating PDF…' : 'Export Quote as PDF'}
        </Button>
      </div>
    </div>
  );
}

